import { Badge, Card, Divider, Flex, theme, Typography } from 'antd';
import type { GlobalToken } from 'antd';
import type { ReactNode } from 'react';
import styled from 'styled-components';

export type TimelineItemTone =
  | 'green'
  | 'red'
  | 'orange'
  | 'neutral'
  | 'blue'
  | 'purple';

export interface TimelineItemProps {
  /** Pre-rendered viewer name/email/fallback. */
  title: ReactNode;
  /** Pre-rendered event copy, e.g. "Paused video at 01:32". */
  description: ReactNode;
  /** Pre-formatted relative time, e.g. "2 min ago". */
  timestamp: ReactNode;
  tone?: TimelineItemTone;
}

const getToneColor = (token: GlobalToken, tone: TimelineItemTone): string => {
  const toneColors: Record<TimelineItemTone, string> = {
    neutral: token.colorTextTertiary,
    green: token.colorPrimaryText,
    red: token.colorError,
    orange: token.colorWarning,
    blue: token.blue,
    purple: token.purple,
  };
  return toneColors[tone];
};

export const TimelineItem = ({
  title,
  description,
  timestamp,
  tone = 'neutral',
}: TimelineItemProps) => {
  const { token } = theme.useToken();

  return (
    <Flex gap={8}>
      <Flex vertical align="center" style={{ height: '100%' }} gap={4}>
        <StyledBadge color={getToneColor(token, tone)} style={{ height: 16 }} />
        <StyledDivider type="vertical" style={{ height: '100%' }} />
      </Flex>
      <Card size="small" style={{ width: '100%' }}>
        <Flex vertical gap={2}>
          <Typography.Text strong>{title}</Typography.Text>
          <Typography.Text>{description}</Typography.Text>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, marginTop: 2 }}
          >
            {timestamp}
          </Typography.Text>
        </Flex>
      </Card>
    </Flex>
  );
};

export default TimelineItem;

// ─── Styled components ───

const StyledBadge = styled(Badge)`
  .ant-badge-status-dot {
    width: 10px !important;
    height: 10px !important;
  }
`;

const StyledDivider = styled(Divider)`
  &.ant-divider-vertical {
    border-inline-start-width: 2px;
  }
`;
